/**
 * This component renders textbox or input box based on the prop isMultiline and passed all the props to them
 */
import React, { FunctionComponent, SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";
import { TextArea } from "components/Novus/Textarea";
import theme from "../../../theme";
import { Input as TextInput, Message, Icon, Main, Image, Label } from "./styles";
import { InputProps } from "./helpers";
import Check from "../../../assets/check.svg";
import Error from "../../../assets/error.svg";
import SearchIcon from "../../../assets/search-white.svg";

export const Input: FunctionComponent<InputProps> = ({
    isMultiline,
    className,
    defaultValue,
    disabled,
    maxLength,
    minLength,
    name,
    value,
    onInputChange,
    onKeyDown,
    isSuccess,
    isFailure,
    successMessage,
    failureMessage,
    type,
    onClick,
    placeholder,
    readOnly,
    isSearchIcon,
    textTransform,
    autoFocus,
    onBlur,
    label,
    onChange,
    step,
    asterisk,
    iconRight
}) => (
    <div className="w-100 d-flex flex-column gap-2">
        {isMultiline ? (
            <TextArea
                aria-label={value}
                asterisk={asterisk}
                className={className}
                defaultValue={defaultValue}
                disabled={disabled}
                label={label}
                maxLength={maxLength}
                minLength={minLength}
                name={name}
                placeholder=""
                rows={1}
                value={value}
                onChange={(event: { target: { value: string } }): void => {
                    if (onInputChange) {
                        onInputChange(event.target.value);
                    }
                }}
                onKeyDown={onKeyDown}
            />
        ) : (
            <SingleLineInput
                autoFocus={autoFocus}
                className={className}
                defaultValue={defaultValue}
                disabled={disabled}
                failureMessage={failureMessage}
                iconRight={iconRight}
                isFailure={isFailure}
                isSearchIcon={isSearchIcon}
                isSuccess={isSuccess}
                label={label}
                maxLength={maxLength}
                minLength={minLength}
                name={name}
                placeholder={placeholder}
                readOnly={readOnly}
                step={step}
                successMessage={successMessage}
                textTransform={textTransform}
                type={type}
                value={value}
                onBlur={onBlur}
                onChange={onChange}
                onClick={onClick}
                onInputChange={onInputChange}
                onKeyDown={onKeyDown}
            />
        )}
    </div>
);

const SingleLineInput: FunctionComponent<InputProps> = ({
    isSuccess,
    isFailure,
    className,
    successMessage,
    failureMessage,
    disabled,
    type,
    minLength,
    maxLength,
    placeholder,
    value,
    defaultValue,
    onInputChange,
    onKeyDown,
    name,
    onClick,
    readOnly,
    textTransform,
    autoFocus,
    onBlur,
    isSearchIcon,
    label,
    onChange,
    step,
    iconRight,
    ...rest
}) => {
    const { t } = useTranslation();

    return (
        <>
            {label && <Label>{t(label)}</Label>}

            <Main>
                <Icon isSearchIcon={isSearchIcon}>
                    {isSuccess && <Image alt="success" src={Check} />}
                    {isFailure && <Image alt="error" src={Error} />}
                    {isSearchIcon && (
                        <div className="search-wrapper">
                            <Image alt="search-icon" className="cursor-pointer" src={SearchIcon} />
                        </div>
                    )}
                </Icon>
                <TextInput
                    className={`form-control ${className || ""}`}
                    defaultValue={defaultValue}
                    disabled={disabled}
                    failureMessage={failureMessage}
                    isFailure={isFailure}
                    isSearchIcon={isSearchIcon}
                    isSuccess={isSuccess}
                    maxLength={maxLength}
                    minLength={minLength}
                    name={name}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    step={step}
                    successMessage={successMessage}
                    textTransform={textTransform}
                    type={type}
                    value={value}
                    onBlur={(event: SyntheticEvent<HTMLInputElement>): void => {
                        if (onBlur) {
                            onBlur(event);
                        }
                    }}
                    onChange={(event): void => {
                        if (onInputChange) {
                            onInputChange(event.target.value);
                        } else if (onChange) {
                            onChange(event);
                        }
                    }}
                    onClick={onClick}
                    onKeyDown={onKeyDown}
                    {...rest}
                    aria-label={name || "input"}
                    autoFocus={autoFocus}
                    theme={theme}
                />

                {iconRight && <div className="icon-right">{iconRight}</div>}

                {(isSuccess || isFailure) && !disabled && (
                    <Message isFailure={isFailure} isSuccess={isSuccess}>
                        {isSuccess && (successMessage || t("Success"))}
                        {isFailure && (failureMessage || t("Error"))}
                    </Message>
                )}
            </Main>
        </>
    );
};

Input.defaultProps = {
    isSearchIcon: false,
    readOnly: false,
    textTransform: "none"
};
SingleLineInput.defaultProps = {
    autoFocus: false,
    readOnly: false,
    textTransform: "none"
};
